/**
 * Extend this union when adding a new modal type. Each variant must carry
 * a `type` discriminant that matches the modal descriptor's `type` field.
 */
type ResolverEntry =
    | { type: "confirm"; resolve: (value: boolean) => void; reject: (reason?: unknown) => void }
    | { type: "info"; resolve: () => void };

/**
 * Module-singleton: one entry per open modal request. Entries are always removed
 * — either via resolve/reject (user action) or via settleExistingSimpleModal
 * (eviction when a new modal opens). This ensures that the returned Promise will always settle.
 */
const resolversByRequestId = new Map<string, ResolverEntry>();

/** Clears all pending resolvers */
export const clearResolverRegistry = (): void => {
    resolversByRequestId.clear();
};

export const registerResolver = (requestId: string, entry: ResolverEntry): void => {
    resolversByRequestId.set(requestId, entry);
};

/**
 * Resolves a confirm modal.
 */
export const resolveConfirm = (requestId: string, value: boolean): void => {
    const entry = resolversByRequestId.get(requestId);
    if (!entry || entry.type !== "confirm") {
        return;
    }
    resolversByRequestId.delete(requestId);
    entry.resolve(value);
};

/**
 * Rejects a confirm modal's promise (e.g. when onConfirm callback throws).
 */
export const rejectConfirm = (requestId: string, reason?: unknown): void => {
    const entry = resolversByRequestId.get(requestId);
    if (!entry || entry.type !== "confirm") {
        return;
    }
    resolversByRequestId.delete(requestId);
    entry.reject(reason);
};

/**
 * Resolves an info modal.
 */
export const resolveInfo = (requestId: string): void => {
    const entry = resolversByRequestId.get(requestId);
    if (!entry || entry.type !== "info") {
        return;
    }
    resolversByRequestId.delete(requestId);
    entry.resolve();
};
